import {useCallback, useMemo, useEffect, useState, useRef} from 'react';
import {ExpandedProduct} from "../types";
import Swatch from './swatch';

interface ProductCardProps {
  product: ExpandedProduct;
  checkpoint: boolean;
  currentCollectionUrl: string;
  lastProduct: string;
}

const ProductCard = ({ product, checkpoint, currentCollectionUrl, lastProduct }: ProductCardProps) => {
  const images = useMemo(() => [...new Set(product.images)], [product.images]);
  let swatches = useMemo(() => [...new Set(product.variants.map(variant => variant.swatch).filter(Boolean))], [product.variants]);
  const variantImages = useMemo(() => [...new Set(product.variant_images)], [product.variant_images]);
  const isCheckpoint = checkpoint;
  const isAxialDebug = localStorage.getItem('axial_debug') === 'true';
  const [selectedVariant, setSelectedVariant] = useState(product.position);
  const [rendered, setRendered] = useState(false);
  const observedElementRef = useRef<HTMLElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const [reorderedDone, setReorderedDone] = useState(false);

  useEffect(() => {
    if (isCheckpoint) {
      if (!observedElementRef.current) {
        const elementToWatch = document.getElementById(`${product.id}-${product.position}`);
        if (elementToWatch) {
          observedElementRef.current = elementToWatch;
        }
      }

      if (observedElementRef.current) {
        if (!observerRef.current) {
          observerRef.current = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                const nextLastProduct = entry.target.id;
                const url = new URL(location.href);
                // If the last product is the same as the next last product, do nothing
                if (url.searchParams.get('last_product') === nextLastProduct) return;

                // Keep only one history entry by always replacing the current URL
                url.searchParams.set('last_product', nextLastProduct);
                history.replaceState({}, '', url.toString());
                
              }
            });
          }, { threshold: 1 });
        }

        observerRef.current.observe(observedElementRef.current);
      }
    }
  }, []);

  useEffect(() => {
    if (`${product.id}-${product.position}` === lastProduct && !rendered) {
      // Scroll the card into view
      const elementToScrollTo = document.getElementById(`${product.id}-${product.position}`);
      if (elementToScrollTo) {
        elementToScrollTo.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center',
        });
      }
    }
    setRendered(true);
  }, []);

  const moneyFormatter = useMemo(
      () =>
          new Intl.NumberFormat(window.currLanguage + '-CA', {
            style: 'currency',
            currency: 'CAD',
          }),
      [selectedVariant]
  );

  const analyseAndDebug = useCallback(() => {
    console.log(product);
  }, [product]);

  const changeSelectedVariant = useCallback((variant: number) => {
        if (isAxialDebug) {
          console.log(`Changing variant to #${variant}`);
          console.log(`Variant ID: ${product.variants[variant].id}`);
          console.log(`Price: ${product.prices[variant].price}`);
          console.log(`Compare at price: ${product.prices[variant].compare_at_price}`);
        }
        // Avoid unnecessary state update if the variant is already selected
        if (selectedVariant !== variant) {
            setSelectedVariant(variant);
        }
      },
      [selectedVariant]
  );

  /**
   * Finds the index of a variant in the product.variants array that matches the given initial_order value.
   * This is used when we have reordered variants to map between the display order and the actual variant index
   * in the original product.variants array.
   * 
   * @param initialOrder - The initial_order value to search for
   * @returns The index of the matching variant in the product.variants array, or -1 if not found
   */
  const findVariantIndexWithMatchingInitialOrder = useCallback((initialOrder: number) => {
    return product.variants.findIndex(variant => variant.initial_order === initialOrder);
  }, [product.variants]);

  const renderSwatches = () => {

    if (product.reorder) {
      // Create an array to collect the variants in the wanted order
      const reorderedVariants: any[] = [];

      // Loop through the reorder array and find the corresponding variant
      product.reorder.forEach((order) => {
        const variant = product.variants.find(variant => variant.initial_order === order);
        if (variant) {
          reorderedVariants.push(variant);
        }
      });

      //On page load,set the selected variant to the first variant in the reordered array
      if (!reorderedDone) {
        let firstVariantFromIndex = reorderedVariants[product.position].initial_order;
        
        //Find the variant in the product.variants array where inital order is the same as the firstVariantFromIndex
        const firstVariantIndex = product.variants.findIndex(variant => variant.initial_order === firstVariantFromIndex);
        if (firstVariantIndex !== -1) {
          setReorderedDone(true);
          setSelectedVariant(firstVariantIndex);
        }
      }

      //render the swatch in the wanted order
      return reorderedVariants.map((variant) => (
        <Swatch
          key={variant.id}
          id={variant.id}
          isActive={findVariantIndexWithMatchingInitialOrder(variant.initial_order) === selectedVariant}
          onClick={() => changeSelectedVariant(findVariantIndexWithMatchingInitialOrder(variant.initial_order))}
          swatchImage={variant.swatch}
        />
      ));
    } else {
      if (swatches.length > 1) {
        return product.variants.map((variant, index) => (

          //Limit the number of swatches to 4
          index < 4 && (
          <Swatch
            key={variant.id}
            id={variant.id}
            isActive={index === selectedVariant}
            onClick={() => changeSelectedVariant(index)}
            swatchImage={variant.swatch}
          />)
        ))
      }
    }
    return null;
  };

  return (
      <div className={'outer-product-card'}>
        {isAxialDebug && (
            <button onClick={analyseAndDebug} className={'debug-button'}>
              D
            </button>
        )}
        <div
            data-page={product.page}
            className={'product-card'}
            id={`${product.id}-${product.position}`}
            data-checkpoint={isCheckpoint}
            data-limoniapps-discountninja-product-handle={product.handle}
            data-limoniapps-discountninja-product-collections={currentCollectionUrl.split('/collections/')[1]}
            data-limoniapps-discountninja-product-available={true}
            data-limoniapps-discountninja-product-price={product.prices[selectedVariant].price}
            data-limoniapps-discountninja-product-compareatprice={product.prices[selectedVariant].compare_at_price}
            data-images={JSON.stringify(images)}
            data-variant-images={JSON.stringify(variantImages)}
        >
          <a
              href={`${currentCollectionUrl}/products/${product.handle}?variant=${product.variants[selectedVariant].id}`}
              className={'image-container'}
              data-product-id={product.id}
              data-product-handle={product.handle}
          >
            <img
                loading={'lazy'}
                className={'product-image'}
                src={`${images[selectedVariant]}&width=720`}
                alt=""
            />
            {product.soon_available && (
                <div className={'soon-available'}>
                  {window.currLanguage === 'fr' ? 'Bientôt disponible' : 'Coming soon'}
                </div>
            )}
            {product.prices[selectedVariant].compare_at_price &&
            parseInt(product.prices[selectedVariant].compare_at_price.toString()) >
            parseInt(product.prices[selectedVariant].price.toString()) ? (
                <span className={'discount-badge'}>
              -
                  {Math.round(
                      100 -
                      (product.prices[selectedVariant].price /
                          product.prices[selectedVariant].compare_at_price) *
                      100
                  )}
                  %
            </span>
            ) : null}
          </a>
          <div className={'product-text-data-and-swatches'}>
            <div className={'product-text-data'}>
              {product.vendor !== 'BLANK' && (
                  <span className={'product-vendor'} dangerouslySetInnerHTML={{__html: product.vendor}}></span>
              )}
              <div className={'product-title-and-price'}>
                <a
                    className={'product-title'}
                    href={`${currentCollectionUrl}/products/${product.handle}?variant=${product.variants[selectedVariant].id}`}
                    dangerouslySetInnerHTML={{__html: product.title}}
                >
                </a>
                <div className={'limoniapps-discountninja-productprice product-price'}>
                  {product.prices[selectedVariant].compare_at_price &&
                  parseInt(product.prices[selectedVariant].compare_at_price.toString()) >
                  parseInt(product.prices[selectedVariant].price.toString()) ? (
                      <>
                    <span className={'compare-at-price'}>
                      {moneyFormatter.format(
                          product.prices[selectedVariant].compare_at_price! / 100
                      )}
                    </span>
                        <span className={'discount-price'}>
                      <span className={'discount-percent'}>
                        -
                        {Math.round(
                            100 -
                            (product.prices[selectedVariant].price /
                                product.prices[selectedVariant].compare_at_price) *
                            100
                        )}
                        %
                      </span>
                          {moneyFormatter.format(product.prices[selectedVariant].price / 100)}
                    </span>
                      </>
                  ) : (
                      <span>
                    {moneyFormatter.format(product.prices[selectedVariant].price / 100)}
                  </span>
                  )}
                </div>
              </div>
            </div>
            <div className={'swatches-container'}>
              {renderSwatches()}
              { swatches.length > 4 && <p>+</p> }
            </div>
          </div>
        </div>
      </div>
  );
};

export default ProductCard;
